import {
  ADD_BEIHILFE_LEGACY_HEADER_TOKEN,
  CredentialsService,
  Deferred,
  DeviceOrigin,
  MessageBase,
  MessageModel,
} from '@ebeihilfe/core';
import { Antrag } from '../types';
import { Directive, computed, inject, signal } from '@angular/core';
import { EinreichungApiService, EinreichungService } from '../services';
import { EinreichungStepResultType } from '../interfaces';
import { HttpContext } from '@angular/common/http';
import { LegacyAntragsMetadata } from '@eBeihilfe/einreichung-shared/model';
import {
  EinreichungApiService as SharedEinreichungApiService,
  EinreichungService as SharedEinreichungService,
} from '@eBeihilfe/einreichung-shared/services';
import { marker as _ } from '@colsen1991/ngx-translate-extract-marker';
import { firstValueFrom, lastValueFrom } from 'rxjs';

@Directive()
export abstract class UploadComponentBase {
  private readonly messageService = inject(MessageBase);
  private readonly credentialsService = inject(CredentialsService);
  private readonly einreichungService = inject(EinreichungService);
  private readonly einreichungApiService = inject(EinreichungApiService);
  private readonly sharedEinreichungApi = inject(SharedEinreichungApiService);
  private readonly sharedEinreichungService = inject(SharedEinreichungService);

  public uploadHasErrored = signal(false);
  public uploadedSeiten = signal(0);
  public allPages = computed(() => this.antrag?.getAllSeiten().length ?? 0);
  public progressParams = computed(() => ({
    pagesDone: this.uploadedSeiten(),
    allPages: this.allPages(),
  }));

  protected antrag: Antrag | undefined;
  protected work?: Deferred<EinreichungStepResultType>;

  public abstract startUpload(): void | Promise<void>;

  public canHandle(antrag: Antrag): boolean {
    return antrag.hasSeiten();
  }

  public destroy(): Promise<void> {
    return Promise.resolve();
  }

  public async discardAntrag(): Promise<void> {
    if (!this.antrag) {
      return;
    }

    if (this.antrag?.antragsId !== undefined) {
      await lastValueFrom(this.sharedEinreichungApi.deleteAntrag(this.antrag.antragsId.toString()));
    }

    this.work?.resolve({
      shouldProceed: false,
      result: this.antrag,
    });
  }

  public hasUi(): boolean {
    return true;
  }

  public async uploadAntrag(origin: DeviceOrigin): Promise<boolean> {
    this.uploadHasErrored.set(false);

    try {
      // in case the Antrag was interrupted before, we dont need to create a new one
      if (!this.antrag?.antragsId?.value) {
        await this.createAntragOnServer(origin);
      }

      await this.uploadPages();
      await this.finishAntrag();

      if (this.antrag?.doNotFinishMe) {
        await this.messageService.showSuccess({ message: _('new_antrag.text.antrag_successful_saved_as_draft') });
      } else {
        await this.messageService.showSuccess({ message: _('new_antrag.text.antrag_successful_eingereicht') });
      }

      this.work?.resolve({
        shouldProceed: true,
        result: this.antrag,
      });
    } catch (err: unknown) {
      if (err instanceof Error && err.message.indexOf('get_antragId.Invalid') > -1) {
        await this.credentialsService.resetPin();
      }

      if (typeof err === 'string') {
        await this.messageService.showError({ message: err });
      }

      await this.messageService.showError(err as MessageModel);
      this.uploadHasErrored.set(true);
    }

    return !this.uploadHasErrored();
  }

  protected async createAntragOnServer(origin: DeviceOrigin): Promise<void> {
    if (!this.antrag) {
      return;
    }

    await this.einreichungApiService.createAntrag(origin, this.antrag);

    if (!(this.antrag?.antragsId && this.antrag?.timestamp)) {
      throw new Error('new_antrag.text.error.antragsId_missing');
    }
  }

  private async finishAntrag(): Promise<void> {
    if (!this.antrag) {
      return;
    }

    if (this.antrag?.doNotFinishMe) {
      return;
    }

    await this.sendMetaData();
    this.antrag?.setComplete();
  }

  private async sendMetaData(): Promise<void> {
    if (!this.antrag) {
      return;
    }

    const encryptedMetadata = await this.sharedEinreichungService.encryptAntragFinishMetadata(
      this.antrag.getAntragsMetadata() as LegacyAntragsMetadata[],
    );

    const antragDto = this.antrag.getAntragFinishData(encryptedMetadata);

    return firstValueFrom(
      this.sharedEinreichungApi.finishEinreichung(antragDto, {
        context: new HttpContext().set(ADD_BEIHILFE_LEGACY_HEADER_TOKEN, true),
      }),
    );
  }

  private async uploadPages(): Promise<void> {
    if (!this.antrag) {
      return;
    }

    for await (const [seite, , uploadSeite] of this.einreichungService.transformToUploadData(this.antrag)) {
      if (!uploadSeite) {
        continue;
      }

      await firstValueFrom(this.sharedEinreichungApi.uploadAnhang(uploadSeite));
      seite.uploaded = true;
      this.uploadedSeiten.update((prev) => prev + 1);
    }
  }
}
